import type Theme from 'style/types/theme';
import styled from 'styled-components';

import { BasicParagraph } from '../../foundations/typefaces/BasicParagraph';
import { SmallParagraph } from '../../foundations/typefaces/SmallParagraph';
import style from '../../style';
import { Color, Size } from './types';

export interface RadioButtonProps {
  disabled?: boolean;
  $size?: Size;
}

interface ParagraphProps {
  disabled: boolean;
  $color?: Color;
}

interface StyledLabelProps extends RadioButtonProps {
  $hasText?: boolean;
  $size?: Size;
}

export const Shadow = styled.div<RadioButtonProps>(
  ({ theme: { size, color }, disabled }) => ({
    height: size.units(4),
    width: size.units(4),
    transform: 'scale(0.375)',
    opacity: 0,
    backgroundColor: disabled
      ? color.background.secondary.default()
      : color.background.accent.default(0.1),
    borderRadius: '50%',
    transition:
      'transform 0.2s cubic-bezier(0,0.4,0.4,1), opacity 0.2s cubic-bezier(0,0.4,0.4,1)',
  }),
);

export const StyledInput = styled.input<RadioButtonProps>(
  ({ theme: { size }, disabled }) => ({
    opacity: '1',
    height: size.units(4),
    width: size.units(4),
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    appearance: 'none',
    boxSizing: 'border-box',
    borderRadius: '50%',

    ...(disabled && {
      pointerEvents: 'none',
      outline: 'none',
      boxShadow: 'none',
      background: 'none',
    }),
  }),
);

export const Checkmark = styled.div<RadioButtonProps>(
  ({ theme: { size, color, transition }, disabled }) => ({
    pointerEvents: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: disabled
      ? color.background.primary.disabled()
      : color.background.primary.default(),
    border: `${size.units(0.25)} solid ${
      disabled ? color.border.quaternary.disabled() : color.border.quaternary.default()
    }`,
    boxSizing: 'content-box',

    '&:after': {
      content: '""',
      left: size.units(0.5),
      top: size.units(0.5),
      background: disabled
        ? color.background.secondary.default()
        : color.background.accent.default(),
      transform: 'scale(0)',
      opacity: '0',
      transition: `${transition.standard(
        'transform',
        'opacity',
      )}, background 0.15s cubic-bezier(0.4,0,0.4,1)`,
    },
  }),
);

export const StyledLabel = styled.label<StyledLabelProps>(
  ({ theme: { size, color }, disabled, $hasText, $size }) => ({
    position: 'relative',
    cursor: disabled ? 'not-allowed' : 'pointer',
    width: size.units(2),
    height: $hasText && $size === Size.SMALL ? undefined : size.units(2),
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    marginTop: $hasText && $size === Size.MEDIUM ? size.units(0.25) : undefined,

    ...(!disabled &&
      style.mixins.devices.desktop({
        [`&:hover ${Shadow}`]: {
          transform: 'scale(1)',
          opacity: 1,
        },

        [`&:active ${Shadow}`]: {
          transform: 'scale(0.75)',
        },

        [`&:hover ${StyledInput}:checked ~ ${Checkmark}:after`]: {
          background: color.background.accent.hover(),
        },
      })),

    [`${StyledInput}:checked ~ ${Checkmark}:after`]: {
      transform: 'scale(1)',
      opacity: '1',
    },

    [StyledInput]: {
      position: 'absolute',
      margin: 0,
      padding: 0,
      zIndex: 1,
    },

    [Shadow]: {
      position: 'absolute',
    },

    [Checkmark]: {
      zIndex: 1,
    },
  }),
);

const getParagraphColor = (color: Theme['color'], disabled: boolean, $color?: Color) => {
  switch ($color) {
    case Color.WHITE:
      return disabled
        ? color.content.reversed.disabled()
        : color.content.reversed.active();
    default:
      return disabled
        ? color.content.secondary.disabled()
        : color.content.secondary.default();
  }
};

export const StyledBasicParagraph = styled(BasicParagraph)<ParagraphProps>(
  ({ theme: { color }, disabled, $color }) => ({
    color: getParagraphColor(color, disabled, $color),
  }),
);

export const StyledSmallParagraph = styled(SmallParagraph)<ParagraphProps>(
  ({ theme: { color }, disabled, $color }) => ({
    color: getParagraphColor(color, disabled, $color),
  }),
);

export const Wrapper = styled.label<RadioButtonProps>(
  ({ theme: { size }, disabled, $size }) => ({
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: $size === Size.SMALL ? 'center' : 'flex-start',

    [StyledBasicParagraph]: {
      marginLeft: size.units(1),
    },
    [StyledSmallParagraph]: {
      marginLeft: size.units(1),
    },
  }),
);
